/**
 * ClientDetail — "where is this MAC?" rendered as an edge Sheet over the Clients list.
 *
 * The single-pane troubleshooting hop: one screen answers, for any client MAC, the whole
 * path  client → SSID/band/signal → AP → switch + port → site,  plus the roaming trail — so an
 * operator never cross-references the AP list, the switch port table and the site by hand.
 * Data comes from GET /clients/{mac}/location (services/client_location), which resolves it from
 * the sighting ledger + live AP state + the AP's LLDP neighbour + a switch FDB read.
 *
 * Read-only. Closing the Sheet navigates back to the list route (the URL owns the state), and
 * the AP / switch nodes deep-link into their own device Sheets in the right site scope.
 */
import { useNavigate, useParams } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { useClientLocation } from "@/lib/api/hooks";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/Page";
import { relTime, fmtBytes, fmtRate, fmtDuration } from "@/lib/format";
import type { ClientLocation, ClientLocationDevice, ClientTrailHop } from "@/types/domain";
import type { ReactNode } from "react";

export default function ClientDetail() {
  const navigate = useNavigate();
  const { mac, orgId, siteId } = useParams();
  const { data, isLoading, error } = useClientLocation(mac);

  // Close returns to the parent list, preserving scope.
  const base = siteId ? `/o/${orgId}/s/${siteId}` : `/o/${orgId}`;
  const close = () => navigate(`${base}/clients`);

  // A device node deep-links into ITS site's device sheet (the AP may live in another site
  // than the one currently selected — the location answer is org-wide).
  const deviceHref = (d: ClientLocationDevice) =>
    d.site_id ? `/o/${d.org_id}/s/${d.site_id}/devices/${d.serial}` : `/o/${d.org_id}/devices/${d.serial}`;

  const title = data ? clientTitle(data) : mac;

  return (
    <Dialog.Root open onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          className="fixed inset-y-0 right-0 z-sheet flex w-full max-w-md flex-col bg-overlay shadow-sheet outline-none"
          aria-describedby={undefined}
        >
          <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-lg font-semibold text-ink">{title}</Dialog.Title>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                <Mono>{data?.mac ?? mac}</Mono>
                {data?.vendor && <span>· {data.vendor}</span>}
                {data && <OnlinePill online={data.online} found={data.found} />}
              </div>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" aria-label="Close">
                ✕
              </Button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {error && <ErrorBanner error={error} />}
            {isLoading && <div className="text-sm text-ink-muted">Locating client…</div>}
            {data && !data.found && (
              <p className="text-sm text-ink-muted">
                Not seen on any managed AP or switch in this scope. Sightings are recorded each
                sync pass; a client that has never associated to a managed AP won't appear here.
              </p>
            )}
            {data && data.found && (
              <div className="flex flex-col gap-5">
                {/* PATH — the chain from the client down to the site, top to bottom. */}
                {(data.path || data.wired) && (
                  <section>
                    <SectionTitle>Path</SectionTitle>
                    <Path data={data} deviceHref={deviceHref} onNavigate={(to) => navigate(to)} />
                  </section>
                )}

                {/* NOW — the live association, only while online. */}
                {data.online && data.current && (
                  <section>
                    <SectionTitle>Now</SectionTitle>
                    <dl className="grid grid-cols-2 gap-3 text-sm">
                      <Field label="IP">
                        <Mono>{data.current.ip ?? "—"}</Mono>
                      </Field>
                      <Field label="Signal">
                        {data.current.rssi != null ? `${data.current.rssi} dBm` : "—"}
                      </Field>
                      <Field label="Rate (↓/↑)">
                        <span className="tabular-nums">
                          {data.current.rx_rate_mbps ?? "—"} / {data.current.tx_rate_mbps ?? "—"} Mbps
                        </span>
                      </Field>
                      <Field label="Retries">
                        {data.current.retry_pct != null ? (
                          <span
                            className={
                              data.current.retry_pct >= 20
                                ? "text-critical"
                                : data.current.retry_pct >= 10
                                  ? "text-warn"
                                  : ""
                            }
                          >
                            {data.current.retry_pct}%
                          </span>
                        ) : (
                          "—"
                        )}
                      </Field>
                      <Field label="Channel">
                        {data.current.channel != null
                          ? `${data.current.channel}${data.current.channel_width ? ` · ${data.current.channel_width} MHz` : ""}`
                          : "—"}
                      </Field>
                      <Field label="Connected">{fmtDuration(data.current.connected_sec)}</Field>
                      {/* Usage for this session. Cumulative bytes since association (context),
                          plus the sustained average over the connected window when the session
                          is long enough to average -- same honesty rule as the Usage table. */}
                      {data.usage && (
                        <>
                          <Field label="Data (↓/↑)">
                            <span className="tabular-nums">
                              {fmtBytes(data.usage.download_bytes)} / {fmtBytes(data.usage.upload_bytes)}
                            </span>
                          </Field>
                          <Field label="Avg rate">
                            <span className="tabular-nums">
                              {data.usage.ratable ? fmtRate(data.usage.avg_bps) : "—"}
                            </span>
                          </Field>
                        </>
                      )}
                    </dl>
                  </section>
                )}

                {/* TRAIL — every AP that has seen this MAC, newest first. */}
                <section>
                  <SectionTitle>Trail ({data.trail.length})</SectionTitle>
                  {data.trail.length === 0 ? (
                    <p className="text-xs text-ink-muted">No wireless sightings recorded.</p>
                  ) : (
                    <ul className="flex flex-col gap-1.5">
                      {data.trail.map((h, i) => (
                        <TrailRow
                          key={`${h.device_serial}-${h.last_seen ?? i}`}
                          hop={h}
                          isCurrent={i === 0 && data.online}
                        />
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * The vertical path: client → radio → AP → switch/port → site. A left rail of dots joined by a
 * hairline reads as "this hangs off that" without a diagram library. Nodes that don't apply
 * (no LLDP neighbour, wired-only client) simply aren't rendered.
 */
function Path({
  data,
  deviceHref,
  onNavigate,
}: {
  data: ClientLocation;
  deviceHref: (d: ClientLocationDevice) => string;
  onNavigate: (to: string) => void;
}) {
  const nodes: ReactNode[] = [];
  const last = data.trail[0];

  // 1. Client.
  nodes.push(
    <PathNode key="client" label="Client">
      <span className="font-medium text-ink">{clientTitle(data)}</span>
    </PathNode>,
  );

  if (data.path) {
    const p = data.path;
    // 2. Radio (live, or the last SSID it was seen on).
    nodes.push(
      <PathNode key="radio" label={data.online ? "Wireless" : "Last wireless"}>
        {data.online && data.current ? (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-ink">{data.current.ssid ?? "—"}</span>
            {data.current.band && <Chip>{data.current.band}</Chip>}
            {data.current.channel != null && (
              <span className="text-xs text-ink-subtle">ch {data.current.channel}</span>
            )}
            {data.current.rssi != null && (
              <span className="text-xs tabular-nums text-ink-subtle">{data.current.rssi} dBm</span>
            )}
            <ExperienceChip value={data.current.experience} />
          </span>
        ) : (
          <span className="text-ink-muted">
            {last?.ssid ?? "—"}
            {last?.band ? ` · ${last.band}` : ""}
            {last?.last_seen ? ` · ${relTime(last.last_seen)}` : ""}
          </span>
        )}
      </PathNode>,
    );
    // 3. AP.
    nodes.push(
      <PathNode key="ap" label="Access point">
        <DeviceLine d={p.ap} href={deviceHref(p.ap)} onNavigate={onNavigate} />
        {p.uplink_port && (
          <div className="mt-0.5 text-xs text-ink-subtle">
            uplink <Mono>{p.uplink_port}</Mono>
          </div>
        )}
      </PathNode>,
    );
    // 4. Switch + port (managed switch resolved, or just what LLDP advertised).
    if (p.switch || p.lldp.system_name) {
      nodes.push(
        <PathNode key="switch" label="Switch port">
          {p.switch ? (
            <DeviceLine d={p.switch} href={deviceHref(p.switch)} onNavigate={onNavigate} />
          ) : (
            <span className="text-ink">
              {p.lldp.system_name}
              <span className="ml-1 text-xs text-ink-subtle">(not managed here)</span>
            </span>
          )}
          <div className="mt-0.5 flex items-center gap-2 text-xs">
            {p.switch_port ? (
              <>
                <span className="text-ink-muted">
                  port <Mono>{p.switch_port}</Mono>
                </span>
                {p.switch_port_source && (
                  <span
                    className={`rounded border px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide ${
                      p.switch_port_source === "fdb"
                        ? "border-healthy/40 text-healthy"
                        : "border-line text-ink-subtle"
                    }`}
                    title={
                      p.switch_port_source === "fdb"
                        ? "Confirmed from the switch MAC table"
                        : "As advertised by the switch over LLDP"
                    }
                  >
                    {p.switch_port_source === "fdb" ? "confirmed" : "via LLDP"}
                  </span>
                )}
              </>
            ) : (
              <span className="text-ink-subtle">port unknown</span>
            )}
            {p.lldp.vlan != null && <span className="text-ink-subtle">· VLAN {p.lldp.vlan}</span>}
          </div>
        </PathNode>,
      );
    }
    // 5. Site.
    nodes.push(
      <PathNode key="site" label="Site" last>
        <span className="text-ink">{p.ap.site_name ?? "Unassigned"}</span>
      </PathNode>,
    );
  } else if (data.wired) {
    // Wired-only: found on a switch port, never on an AP.
    const w = data.wired;
    nodes.push(
      <PathNode key="wired" label="Switch port">
        <DeviceLine d={w.switch} href={deviceHref(w.switch)} onNavigate={onNavigate} />
        <div className="mt-0.5 text-xs text-ink-muted">
          port <Mono>{w.port ?? "?"}</Mono>
          {w.vlan != null ? ` · VLAN ${w.vlan}` : ""}
          <span className="ml-2 rounded border border-healthy/40 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-healthy">
            wired
          </span>
        </div>
      </PathNode>,
    );
    nodes.push(
      <PathNode key="site" label="Site" last>
        <span className="text-ink">{w.switch.site_name ?? "Unassigned"}</span>
      </PathNode>,
    );
  }

  return <ol className="flex flex-col">{nodes}</ol>;
}

/** One rung of the path: a dot on the left rail (joined to the next by a hairline) + content. */
function PathNode({ label, last, children }: { label: string; last?: boolean; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <div className="flex w-3 shrink-0 flex-col items-center">
        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full border-2 border-brand bg-overlay" />
        {!last && <span className="w-px flex-1 bg-line" />}
      </div>
      <div className={`min-w-0 flex-1 text-sm ${last ? "" : "pb-4"}`}>
        <div className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </li>
  );
}

/** A device reference inside the path: name, model, and an "Open" jump into its own sheet. */
function DeviceLine({
  d,
  href,
  onNavigate,
}: {
  d: ClientLocationDevice;
  href: string;
  onNavigate: (to: string) => void;
}) {
  return (
    <span className="flex flex-wrap items-center gap-x-2">
      <span className="font-medium text-ink">{d.name}</span>
      {d.model && <span className="text-xs text-ink-subtle">{d.model}</span>}
      <button type="button" onClick={() => onNavigate(href)} className="text-xs font-medium text-brand hover:underline">
        Open →
      </button>
    </span>
  );
}

/** One roaming-trail row: AP · site · SSID · when. The top row is the current AP when online. */
function TrailRow({ hop, isCurrent }: { hop: ClientTrailHop; isCurrent: boolean }) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-md border border-line px-3 py-2 text-sm">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-ink">{hop.device_name}</span>
          {isCurrent && (
            <span className="rounded bg-healthy/10 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-healthy">
              now
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-ink-muted">
          {hop.site_name ?? "Unassigned"}
          {hop.ssid ? ` · ${hop.ssid}` : ""}
          {hop.band ? ` · ${hop.band}` : ""}
          {hop.ip ? ` · ${hop.ip}` : ""}
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-ink-subtle">
        <div>{relTime(hop.last_seen)}</div>
        {hop.first_seen && hop.first_seen !== hop.last_seen && (
          <div className="text-[0.6875rem]">since {relTime(hop.first_seen)}</div>
        )}
      </div>
    </li>
  );
}

/** Best display name for the client: hostname, else "<vendor> device", else the MAC. */
function clientTitle(d: ClientLocation): string {
  if (d.hostname) return d.hostname;
  if (d.vendor && d.vendor !== "Randomized") return `${d.vendor} device`;
  return d.mac;
}

function OnlinePill({ online, found }: { online: boolean; found: boolean }) {
  if (!found) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide ${
        online ? "bg-healthy/10 text-healthy" : "bg-surface-2 text-ink-subtle"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${online ? "bg-healthy" : "bg-ink-subtle"}`} />
      {online ? "online" : "offline"}
    </span>
  );
}

/** Link-quality chip (same tones as the Clients list). */
function ExperienceChip({ value }: { value?: string | null }) {
  if (!value || value === "unknown") return null;
  const tone =
    value === "excellent" || value === "good"
      ? "text-healthy border-healthy/40"
      : value === "fair"
        ? "text-warn border-warn/40"
        : "text-critical border-critical/40";
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[0.6875rem] font-medium capitalize ${tone}`}>
      {value}
    </span>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem]">{children}</span>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">{children}</h3>
  );
}

function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono text-[0.8125rem]">{children}</span>;
}
